import { AxiosResponse } from 'axios';
import axios from 'axios';
import { setupInterceptorsTo } from '../axios';
import metaEnv from '../../helper/metaEnv';
import {
  IUserWithBranches,
  IUserWithBranchesAPIResponse,
  IBranch,
  IDashboardStats,
  IDashboardStatsResponse,
  IAssignBranchRequest,
  IRemoveBranchRequest,
  IRemoveAllBranchesRequest,
  IRemoveAllBranchesResponse,
  IUserBranchAPIResponse,
  IUserBranchRelationship,
  // User Branches Response Interfaces
  IUserBranchesResponse,
  ISetDefaultBranchRequest,
  ISetDefaultBranchResponse,
  // Admin User Management Interfaces
  IAdminBranchDetailResponse,
  IAdminAddUserToBranchRequest,
  IAdminAddUserToBranchResponse,
  IAdminSetPrimaryUserRequest,
  IAdminSetPrimaryUserResponse,
  IAdminRemoveUserFromBranchResponse,
  IAdminUsersQueryParams,
  IAdminUsersListResponse,
  IAdminUpdateUserPhoneRequest,
  IAdminUpdateUserPhoneResponse,
  IAdminDemoteToSecondaryRequest,
  IAdminDemoteToSecondaryResponse,
  IAdminSetUserCustomerCompaniesRequest,
  IAdminUserCustomerCompaniesResponse,
} from '../../compiler/userBranch.interface';

// Create axios instance with farmbook API base URL
const axiosInstance = setupInterceptorsTo(axios.create(), metaEnv.baseAPI);

/**
 * ========================================
 * User-Branch Management API Collection
 * ========================================
 * 
 * API Endpoints สำหรับจัดการการจับคู่ระหว่าง User และ Branch:
 * 
 * 1. GET /user-customer-branch/admin/users-with-branches - หน้าจัดการ User-Branch
 * 2. GET /user-customer-branch/by-line-user-id - Main API สำหรับแอป
 * 3. GET /user-customer-branch/migration-status - ตรวจสอบ Migration
 * 4. POST /user-customer-branch/migrate-existing-data - Migration tool
 * 5. POST /user-customer-branch/assign-branch - กำหนดสาขาให้ user
 * 6. POST /user-customer-branch/remove-branch - ยกเลิกสาขาจาก user
 * 7. POST /user-customer-branch/remove-all-branches - ลบสาขาทั้งหมดจาก user
 * 8. GET /user-customer-branch/user/:userUuid/branches - ดูสาขาของ user
 * 9. GET /procurement/customer-branch - ดูสาขาทั้งหมด (เปลี่ยนจาก /user-customer-branch/admin/branches)
 * 10. GET /user-customer-branch/admin/dashboard-stats - สถิติสำหรับ Dashboard
 */

// Admin APIs - Get Users with Branches (จุดประสงค์: ดึงรายการ Users ทั้งหมดพร้อมสาขาที่จับคู่ พร้อม Pagination และ Filters)
// Query Parameters:
// - keyword: ค้นหาจาก fullname, username, line_display_name, phone
// - branch_id: Filter users ที่มีสาขานี้
// - has_branches: Filter users ที่มี/ไม่มีสาขา (true/false)
// - status: Filter ตามสถานะ (active/inactive)
// - page: หน้าที่ต้องการ (default: 1)
// - limit: จำนวนต่อหน้า (default: 15)
// - sort_by: เรียงตาม (fullname/created_at/branch_count)
// - sort_order: ลำดับการเรียง (asc/desc)
export const APIGetUsersWithBranches = async (params?: any): Promise<IUserWithBranchesAPIResponse> => {
  const response: AxiosResponse<IUserWithBranchesAPIResponse> = await axiosInstance.get(
    '/user-customer-branch/admin/users-with-branches',
    { params }
  );
  return response.data;
};

// Admin APIs - Get All Branches (with pagination support)
export const APIGetAllBranches = async (params?: {
  page?: number;
  limit?: number;
  search?: string;
  franchise_uuid?: string;
  province?: string;
  district?: string;
}): Promise<{
  data: IBranch[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}> => {
  const response: AxiosResponse<{
    data: IBranch[];
    meta: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  }> = await axiosInstance.get(
    '/customer-branch',
    { params }
  );
  
  // API now returns the expected format with data and meta
  return response.data;
};

// Admin APIs - Get All Branches Without Pagination (for selection modals)
export const APIGetAllBranchesNoPagination = async (params?: {
  search?: string;
  franchise_uuid?: string;
  province?: string;
  district?: string;
}): Promise<IBranch[]> => {
  const response: AxiosResponse<IBranch[]> = await axiosInstance.get(
    '/customer-branch/all-branches',
    { params }
  );
  
  return response.data;
};

// Admin APIs - Get Dashboard Stats
export const APIGetDashboardStats = async (): Promise<IDashboardStatsResponse> => {
  const response: AxiosResponse<IDashboardStatsResponse> = await axiosInstance.get(
    '/user-customer-branch/admin/dashboard-stats'
  );
  return response.data;
};

// Get User Branches by User UUID
export const APIGetUserBranches = async (userUuid: string): Promise<IUserBranchesResponse> => {
  const response: AxiosResponse<IUserBranchesResponse> = await axiosInstance.get(
    `/user-customer-branch/user/${userUuid}/branches`
  );
  return response.data;
};

// Set Default Branch for User
export const APISetDefaultBranch = async (
  data: ISetDefaultBranchRequest
): Promise<ISetDefaultBranchResponse> => {
  const response: AxiosResponse<ISetDefaultBranchResponse> = await axiosInstance.patch(
    '/user-customer-branch/set-default-branch',
    data
  );
  return response.data;
};

// Assign Branch to User
export const APIAssignBranchToUser = async (
  data: IAssignBranchRequest
): Promise<IUserBranchAPIResponse> => {
  const response: AxiosResponse<IUserBranchAPIResponse> = await axiosInstance.post(
    '/user-customer-branch/assign-branch',
    data
  );
  return response.data;
};

// Remove Branch from User
export const APIRemoveBranchFromUser = async (
  data: IRemoveBranchRequest
): Promise<IUserBranchAPIResponse> => {
  const response: AxiosResponse<IUserBranchAPIResponse> = await axiosInstance.post(
    '/user-customer-branch/remove-branch',
    data
  );
  return response.data;
};

// Remove All Branches from User
export const APIRemoveAllBranchesFromUser = async (
  data: IRemoveAllBranchesRequest
): Promise<IRemoveAllBranchesResponse> => {
  const response: AxiosResponse<IRemoveAllBranchesResponse> = await axiosInstance.post(
    '/user-customer-branch/remove-all-branches',
    data
  );
  return response.data;
};

// Get Migration Status
export const APIGetMigrationStatus = async (): Promise<any> => {
  const response: AxiosResponse<any> = await axiosInstance.get(
    '/user-customer-branch/migration-status'
  );
  return response.data;
};

// Migrate Existing Data
export const APIMigrateExistingData = async (data?: any): Promise<any> => {
  const response: AxiosResponse<any> = await axiosInstance.post(
    '/user-customer-branch/migrate-existing-data',
    data
  );
  return response.data;
};

// Get User-Branch Relationships by User ID (for line app)
export const APIGetUserBranchByLineUserId = async (lineUserId: string): Promise<IUserBranchRelationship[]> => {
  const response: AxiosResponse<IUserBranchRelationship[]> = await axiosInstance.get(
    `/user-customer-branch/by-line-user-id?line_user_id=${lineUserId}`
  );
  return response.data;
};

// Get User-Branch Relationships by User UUID (internal use)
export const APIGetUserBranchByUserUuid = async (userUuid: string): Promise<IUserBranchRelationship[]> => {
  const response: AxiosResponse<IUserBranchRelationship[]> = await axiosInstance.get(
    `/user-customer-branch/by-user-uuid?user_uuid=${userUuid}`
  );
  return response.data;
};

// =====================================================
// Admin User Management APIs (Multi-User Branch System)
// =====================================================

/**
 * Get Branch Detail with Users (Admin API)
 * Endpoint: GET /admin/user-management/branch/:uuid/detail
 * Returns branch information with all associated LINE users
 */
export const APIAdminGetBranchDetail = async (branchUuid: string): Promise<IAdminBranchDetailResponse> => {
  const response: AxiosResponse<IAdminBranchDetailResponse> = await axiosInstance.get(
    `/admin/user-management/branch/${branchUuid}/detail`
  );
  return response.data;
};

/**
 * Get Users List (Admin API)
 * Endpoint: GET /admin/user-management/users
 * Returns paginated list of users with their branch associations
 */
export const APIAdminGetUsersList = async (params?: IAdminUsersQueryParams): Promise<IAdminUsersListResponse> => {
  // Ensure default values for pagination and convert to integers
  const queryParams: Record<string, any> = {
    page: Number(params?.page) || 1,
    limit: Number(params?.limit) || 20,
  };
  
  // Add optional filters only if they have values
  if (params?.search && params.search.trim()) {
    queryParams.search = params.search.trim();
  }
  if (params?.franchise_uuid) {
    queryParams.franchise_uuid = params.franchise_uuid;
  }
  if (params?.branch_uuid) {
    queryParams.branch_uuid = params.branch_uuid;
  }
  if (params?.role) {
    queryParams.role = params.role;
  }
  
  const response: AxiosResponse<IAdminUsersListResponse> = await axiosInstance.get(
    '/admin/user-management/users',
    { params: queryParams }
  );
  return response.data;
};

/**
 * Get Customer Company Scope for User (Admin API)
 * Endpoint: GET /admin/user-management/user/:user_uuid/customer-companies
 */
export const APIAdminGetUserCustomerCompanies = async (
  userUuid: string
): Promise<IAdminUserCustomerCompaniesResponse> => {
  const response: AxiosResponse<IAdminUserCustomerCompaniesResponse> = await axiosInstance.get(
    `/admin/user-management/user/${userUuid}/customer-companies`
  );
  return response.data;
};

/**
 * Set Customer Company Scope for User (Admin API)
 * Endpoint: PUT /admin/user-management/user/:user_uuid/customer-companies
 */
export const APIAdminSetUserCustomerCompanies = async ({
  user_uuid,
  ...data
}: IAdminSetUserCustomerCompaniesRequest & { user_uuid: string }): Promise<IAdminUserCustomerCompaniesResponse> => {
  const response: AxiosResponse<IAdminUserCustomerCompaniesResponse> = await axiosInstance.put(
    `/admin/user-management/user/${user_uuid}/customer-companies`,
    data
  );
  return response.data;
};

/**
 * Add User to Branch (Admin API)
 * Endpoint: POST /admin/user-management/add-user-to-branch
 * Allows admin to directly add a user to a branch with specified role
 */
export const APIAdminAddUserToBranch = async (
  data: IAdminAddUserToBranchRequest
): Promise<IAdminAddUserToBranchResponse> => {
  const response: AxiosResponse<IAdminAddUserToBranchResponse> = await axiosInstance.post(
    '/admin/user-management/add-user-to-branch',
    data
  );
  return response.data;
};

/**
 * Set Primary User (Admin API)
 * Endpoint: POST /admin/user-management/set-primary-user
 * Sets a user as the PRIMARY user for a branch (admin override)
 * If force=true, will swap existing PRIMARY user to SECONDARY
 */
export const APIAdminSetPrimaryUser = async (
  data: IAdminSetPrimaryUserRequest
): Promise<IAdminSetPrimaryUserResponse> => {
  const response: AxiosResponse<IAdminSetPrimaryUserResponse> = await axiosInstance.post(
    '/admin/user-management/set-primary-user',
    data
  );
  return response.data;
};

/**
 * Remove User from Branch (Admin API)
 * Endpoint: DELETE /admin/user-management/user/:user_uuid/branch/:branch_uuid
 * Removes user's access to a specific branch
 */
export const APIAdminRemoveUserFromBranch = async (
  userUuid: string,
  branchUuid: string
): Promise<IAdminRemoveUserFromBranchResponse> => {
  const response: AxiosResponse<IAdminRemoveUserFromBranchResponse> = await axiosInstance.delete(
    `/admin/user-management/user/${userUuid}/branch/${branchUuid}`
  );
  return response.data;
};

/**
 * Update User Phone (Admin API)
 * Endpoint: PATCH /admin/user-management/update-user-phone
 * Allows admin to update phone and/or verified_phone of a user directly
 */
export const APIAdminUpdateUserPhone = async (
  data: IAdminUpdateUserPhoneRequest
): Promise<IAdminUpdateUserPhoneResponse> => {
  const response: AxiosResponse<IAdminUpdateUserPhoneResponse> = await axiosInstance.patch(
    '/admin/user-management/update-user-phone',
    data
  );
  return response.data;
};
/**
 * Demote User to Secondary (Admin API)
 * Endpoint: PATCH /admin/user-management/demote-to-secondary
 * Demotes a PRIMARY user to SECONDARY, leaving the branch without a primary (ready for re-registration)
 */
export const APIAdminDemoteToSecondary = async (
  data: IAdminDemoteToSecondaryRequest
): Promise<IAdminDemoteToSecondaryResponse> => {
  const response: AxiosResponse<IAdminDemoteToSecondaryResponse> = await axiosInstance.patch(
    '/admin/user-management/demote-to-secondary',
    data
  );
  return response.data;
};
